import { useState, useMemo, useCallback } from 'react';
import { serializeData } from '../utils/helperUtils';
import { blocksToMarkdown } from '../utils/markdownUtils';

/**
 * Hook to manage content editor state and handlers (block editor, tables, side panel)
 */
export const useContentEditor = (activeSection, editableData, setEditableData) => {
    const [selectedCell, setSelectedCell] = useState(null);
    const [localBlocks, setLocalBlocks] = useState([]);
    const [isEditingContent, setIsEditingContent] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [originalContent, setOriginalContent] = useState(null);

    const addToHistory = useCallback((blocks, cell) => {
        const newState = { blocks: JSON.parse(JSON.stringify(blocks)), cell: JSON.parse(JSON.stringify(cell)) };
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newState);
        if (newHistory.length > 50) newHistory.shift();
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [history, historyIndex]);

    const syncData = useCallback((newSerializedValue, newRawValue = null) => {
        if (!selectedCell) return;
        const { section = activeSection, subKey } = selectedCell;

        setEditableData(prev => {
            const newData = { ...prev };
            // Store HTML string directly so handleUpdate sends it as-is to the backend
            const valueToStore = newSerializedValue;

            if (subKey !== undefined) {
                if (typeof newData[section] === 'object' && newData[section] !== null && !Array.isArray(newData[section])) {
                    newData[section] = { ...newData[section], [subKey]: valueToStore };
                }
            } else {
                newData[section] = valueToStore;
            }
            return newData;
        });
    }, [selectedCell, activeSection, setEditableData]);

    const undo = useCallback(() => {
        if (historyIndex <= 0) return;
        const prevState = history[historyIndex - 1];
        setLocalBlocks(prevState.blocks);
        setSelectedCell(prevState.cell);
        setHistoryIndex(historyIndex - 1);
        // Sync with editableData
        const newMarkdown = blocksToMarkdown(prevState.blocks);
        syncData(newMarkdown, prevState.cell?.rawValue);
    }, [history, historyIndex, syncData]);

    const redo = useCallback(() => {
        if (historyIndex >= history.length - 1) return;
        const nextState = history[historyIndex + 1];
        setLocalBlocks(nextState.blocks);
        setSelectedCell(nextState.cell);
        setHistoryIndex(historyIndex + 1);
        const newMarkdown = blocksToMarkdown(nextState.blocks);
        syncData(newMarkdown, nextState.cell?.rawValue);
    }, [history, historyIndex, syncData]);

    const handleContentClick = useCallback((section, subKey, type, value) => {
        setIsEditingContent(false);
        if (selectedCell && selectedCell.section === section && selectedCell.subKey === subKey) {
            setSelectedCell(null);
            return;
        }
        const serialized = serializeData(value, type);
        const newCell = { section, subKey, type, value: serialized, rawValue: value };
        setSelectedCell(newCell);
    }, [selectedCell]);

    const handleSidePanelEdit = useCallback((newSerializedValue, newRawValue = null) => {
        // Update selectedCell state for legacy table editor compatibility
        if (selectedCell) {
            setSelectedCell(prev => ({
                ...prev,
                value: newSerializedValue,
                rawValue: newRawValue || prev?.rawValue
            }));
            syncData(newSerializedValue, newRawValue);
        } else {
            // Unified Tiptap editor: update editableData directly by section
            setEditableData(prev => ({
                ...prev,
                [activeSection]: newSerializedValue
            }));
        }
    }, [selectedCell, activeSection, setEditableData, syncData]);

    const handleBlockEdit = useCallback((index, newContent) => {
        const updatedBlocks = [...localBlocks];
        updatedBlocks[index].content = newContent;
        setLocalBlocks(updatedBlocks);
        const newMarkdown = blocksToMarkdown(updatedBlocks);
        handleSidePanelEdit(newMarkdown);
        // We don't add to history on every keystroke, maybe on blur or debounced
    }, [localBlocks, handleSidePanelEdit]);

    const handleTableCellEdit = useCallback((rowIdx, header, newValue) => {
        if (!selectedCell || selectedCell.type !== 'table') return;
        const updatedRawValue = [...selectedCell.rawValue];
        updatedRawValue[rowIdx] = { ...updatedRawValue[rowIdx], [header]: newValue };
        const tableMarkdown = serializeData(updatedRawValue, 'table');
        const newCell = { ...selectedCell, value: tableMarkdown, rawValue: updatedRawValue };
        setSelectedCell(newCell);

        // Update the table block inside localBlocks and serialize ALL blocks to full Markdown
        const tableBlockIdx = selectedCell.index !== undefined
            ? selectedCell.index
            : localBlocks.findIndex(b => b.type === 'table');
        const updatedBlocks = [...localBlocks];
        if (tableBlockIdx >= 0 && updatedBlocks[tableBlockIdx]) {
            updatedBlocks[tableBlockIdx] = { ...updatedBlocks[tableBlockIdx], content: tableMarkdown };
            setLocalBlocks(updatedBlocks);
        }
        const fullMarkdown = blocksToMarkdown(tableBlockIdx >= 0 ? updatedBlocks : localBlocks);
        syncData(fullMarkdown || tableMarkdown);
    }, [selectedCell, localBlocks, syncData]);

    const handleTableAction = useCallback((action) => {
        if (!selectedCell || selectedCell.type !== 'table' || !Array.isArray(selectedCell.rawValue)) return;
        let updatedRawValue = [...selectedCell.rawValue];
        const headers = Object.keys(updatedRawValue[0] || {});

        if (action === 'addRow') {
            const newRow = {};
            headers.forEach(h => newRow[h] = '');
            updatedRawValue.push(newRow);
        } else if (action === 'addColumn') {
            const newColName = `Column ${headers.length + 1}`;
            updatedRawValue = updatedRawValue.map(row => ({ ...row, [newColName]: '' }));
        } else if (action === 'deleteRow') {
            if (updatedRawValue.length > 1) {
                const rowIndex = window.prompt(`Enter row index to delete (1 to ${updatedRawValue.length}):`);
                const idx = parseInt(rowIndex) - 1;
                if (!isNaN(idx) && idx >= 0 && idx < updatedRawValue.length) {
                    updatedRawValue.splice(idx, 1);
                } else if (rowIndex === null) {
                    return;
                } else {
                    updatedRawValue.pop();
                }
            }
        } else if (action === 'deleteColumn') {
            if (headers.length > 1) {
                const colName = window.prompt(`Enter column name to delete (${headers.join(', ')}):`);
                if (colName && headers.includes(colName)) {
                    updatedRawValue = updatedRawValue.map(row => {
                        const newRow = { ...row };
                        delete newRow[colName];
                        return newRow;
                    });
                } else if (colName === null) {
                    return;
                } else {
                    const lastCol = headers[headers.length - 1];
                    updatedRawValue = updatedRawValue.map(row => {
                        const newRow = { ...row };
                        delete newRow[lastCol];
                        return newRow;
                    });
                }
            }
        }

        const tableMarkdown = serializeData(updatedRawValue, 'table');
        const newCell = { ...selectedCell, value: tableMarkdown, rawValue: updatedRawValue };
        setSelectedCell(newCell);

        // Update the table block inside localBlocks and serialize ALL blocks to full Markdown
        const tableBlockIdx = selectedCell.index !== undefined
            ? selectedCell.index
            : localBlocks.findIndex(b => b.type === 'table');
        const updatedBlocks = [...localBlocks];
        if (tableBlockIdx >= 0 && updatedBlocks[tableBlockIdx]) {
            updatedBlocks[tableBlockIdx] = { ...updatedBlocks[tableBlockIdx], content: tableMarkdown };
            setLocalBlocks(updatedBlocks);
        }
        const fullMarkdown = blocksToMarkdown(tableBlockIdx >= 0 ? updatedBlocks : localBlocks);
        syncData(fullMarkdown || tableMarkdown);
        addToHistory(tableBlockIdx >= 0 ? updatedBlocks : localBlocks, newCell);
    }, [selectedCell, localBlocks, syncData, addToHistory]);

    const handleRenameColumn = useCallback((oldName, newName) => {
        if (!selectedCell || selectedCell.type !== 'table' || !Array.isArray(selectedCell.rawValue) || !newName) return;
        const updatedRawValue = selectedCell.rawValue.map(row => {
            const newRow = {};
            Object.keys(row).forEach(key => {
                if (key === oldName) {
                    newRow[newName] = row[key];
                } else {
                    newRow[key] = row[key];
                }
            });
            return newRow;
        });

        const tableMarkdown = serializeData(updatedRawValue, 'table');
        const newCell = { ...selectedCell, value: tableMarkdown, rawValue: updatedRawValue };
        setSelectedCell(newCell);

        // Update the table block inside localBlocks and serialize ALL blocks to full Markdown
        const tableBlockIdx = selectedCell.index !== undefined
            ? selectedCell.index
            : localBlocks.findIndex(b => b.type === 'table');
        const updatedBlocks = [...localBlocks];
        if (tableBlockIdx >= 0 && updatedBlocks[tableBlockIdx]) {
            updatedBlocks[tableBlockIdx] = { ...updatedBlocks[tableBlockIdx], content: tableMarkdown };
            setLocalBlocks(updatedBlocks);
        }
        const fullMarkdown = blocksToMarkdown(tableBlockIdx >= 0 ? updatedBlocks : localBlocks);
        syncData(fullMarkdown || tableMarkdown);
        addToHistory(tableBlockIdx >= 0 ? updatedBlocks : localBlocks, newCell);
    }, [selectedCell, localBlocks, syncData, addToHistory]);

    const startEditing = useCallback(() => {
        if (!editableData || !activeSection) return;
        setOriginalContent(editableData[activeSection]);
        setIsEditingContent(true);
    }, [editableData, activeSection]);

    /**
     * Cancel: discard ONLY the local user's unsaved edits.
     * Optionally accepts `latestSavedContent` (already fetched from backend)
     * so the view mode shows the latest saved content, not the old snapshot.
     */
    const cancelEditing = useCallback((latestSavedContent) => {
        const contentToRestore = latestSavedContent !== undefined
            ? latestSavedContent
            : originalContent;
        if (contentToRestore !== null && activeSection) {
            setEditableData(prev => ({
                ...prev,
                [activeSection]: contentToRestore
            }));
        }
        setIsEditingContent(false);
        setOriginalContent(null);
    }, [originalContent, activeSection, setEditableData]);

    /**
     * Silently update editor/view state with fresh content from backend
     * without closing edit mode.
     * Only applies if the user has NOT made any local changes yet.
     */
    const applyRemoteContent = useCallback((sectionKey, freshContent) => {
        if (!sectionKey) return;
        setEditableData(prev => ({
            ...prev,
            [sectionKey]: freshContent
        }));
        // Also refresh the originalContent baseline so a subsequent cancel
        // returns to the most-recently-saved backend content.
        if (sectionKey === activeSection) {
            setOriginalContent(freshContent);
        }
    }, [activeSection, setEditableData]);

    return useMemo(() => ({
        selectedCell,
        setSelectedCell,
        localBlocks,
        setLocalBlocks,
        isEditingContent,
        setIsEditingContent,
        handleContentClick,
        handleBlockEdit,
        handleTableCellEdit,
        handleSidePanelEdit,
        handleTableAction,
        handleRenameColumn,
        cancelEditing,
        addToHistory,
        undo,
        redo,
        syncData,
        startEditing,
        originalContent,
        applyRemoteContent,
    }), [
        selectedCell,
        localBlocks,
        isEditingContent,
        addToHistory,
        undo,
        redo,
        syncData,
        handleContentClick,
        handleBlockEdit,
        handleTableCellEdit,
        handleSidePanelEdit,
        handleTableAction,
        handleRenameColumn,
        startEditing,
        cancelEditing,
        originalContent,
        applyRemoteContent,
    ]);
};
